import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Col, Container, Modal, Row, Spinner } from 'react-bootstrap';
import { selectOrders } from "../features/orders/ordersSlice";
import OrderObj from '../features/orders/orderObj';
import { selectToken } from '../features/user/userSlice';
import OrderDetails from "../features/orders/OrderDetails";
import BillingAndShipping from "../features/orders/BillingAndShipping";
import './orderReceivedPage.scss';
import moment from 'moment';
import { ICartItems } from "../features/cart/interfaces";
import { selectCustomer } from '../features/customer/customerSlice';
import CustomerObj from '../features/customer/customerObj';
import { selectProducts } from '../features/products/productsSlice';
import { setCart } from '../features/cart/cartSlice';
import { selectIsMobileRoute } from '../features/mobile/mobileSlice';
import Loader from '../components/Loader';
import ThankyouPageOffers from './ThankyouPageOffers';

export default function OrderReceivedPage() {
  let params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector(selectToken);
  const products = useSelector(selectProducts);
  const orders = useSelector(selectOrders);
  const order = params.orderId ? OrderObj.findOrder(orders, Number(params.orderId)) : null;
  const mightyPoints = order ? order.getEarnedPoints() : 0;
  const deliveryDate = order ? order.getDeliveryDate() : null;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isTimerOn, setIsTimerOn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [remainingHours, setRemainingHours] = useState('');
  const [remainingMinutes, setRemainingMinutes] = useState('');
  const [remainingSeconds, setRemainingSeconds] = useState('');
  const [showVideo, setShowVideo] = useState(false);
  const [customerOrder, setCustomerOrder] = useState<ICartItems | null>(null);
  const custData = useSelector(selectCustomer);
  const AppURL = useSelector(selectIsMobileRoute);
  const QURLParams = AppURL ? `/?${AppURL}` : '/';
  const AndURLParams = AppURL ? `&${AppURL}` : '';
  const customer = useMemo(() => {
    return new CustomerObj(custData);
  }, [custData]);
  const couponCodes = order?.data?.coupon_lines?.map((item: any) => item.code) || [];

  useEffect(() => {
    if (orders.length <= 1) {
      window.scrollTo(0, 0);
      setShowVideo(true);
    }
  }, [orders])

  useEffect(() => {
    if (order && order?.data) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [order, params.orderId])

  useEffect(() => {
    const cartItems = order?.lineItemsToCartItems(products, customer);
    if (cartItems) {
      setCustomerOrder(cartItems)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!order?.data.date_paid_gmt) {
      setIsTimerOn(false);
      return;
    }

    if (!customer.hasMembership() && !(
      couponCodes.some((code: string) => /^(meal100|mighty100)\d*(-\d+)?$/.test(code)) ||
      couponCodes.some((code: string) => /^(meal100|mighty100)\d+-\d+$/.test(code))
    )
    ) {
      const countDownDate = moment.utc(order.data.date_paid_gmt).local().add(2, 'hours').toDate().getTime();

      const intervalId = setInterval(() => {
        setIsTimerOn(true);
        const now = new Date().getTime();
        const distance = countDownDate - now;
        const hours = Math.floor(distance / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setRemainingHours(`${hours}`);
        setRemainingMinutes(`${minutes}`);
        setRemainingSeconds(`${seconds}`);

        if (distance < 0) {
          setIsTimerOn(false);
          clearInterval(intervalId);
          setRemainingHours('0');
          setRemainingMinutes('0');
          setRemainingSeconds('0');
        }
      }, 1000);

      return () => {
        clearInterval(intervalId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.data.date_paid_gmt]);

  useEffect(() => {
    if (!token) {
      navigate(`${QURLParams}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const handlePlaceOrder = async () => {
    setPlacing(true)
    if (customerOrder) {
      await dispatch(setCart({ token: token, cart_items: customerOrder }));
      navigate(`/checkout/?coupon-code=10offnextorder${AndURLParams}`)
    }
    setPlacing(false)
  }

  return (
    <div className='order-received-page mt-0'>
      <Modal show={showVideo} onHide={() => setShowVideo(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Welcome to MightyMeals!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
            <video
              controls
              autoPlay
              playsInline
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            >
              <source src="https://eatmightymeals.com/thank-you-from-mightymeals_small.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </Modal.Body>
      </Modal>
      {isTimerOn ?
        (<Alert className={`text-center text-white orderTimer py-4 ${isLoading ? 'blur-bg' : ''}`} variant="success">
          <div className='PlaceNextOrder container'>
            <Row>
              <Col xs={6} className=" first-col orderleft-wrap">
                <span className='PlaceNextOrderText'>Place future orders within 2 hours and receive 10% OFF!</span>
                <Button
                  onClick={handlePlaceOrder}
                  disabled={placing}
                  size="sm"
                  variant="dark"
                  className=''
                >
                  {placing ?
                    (<>
                      Placing order ...
                      <Spinner
                        animation="border"
                        as="span"
                        size="sm"
                      />
                    </>)
                    : 'Reorder'}
                </Button>
              </Col>
              <Col xs={6} className="orderright-wrap">
                <div className='ot-grid'>
                  <div className="ot-grid-box">
                    <span className='ot-grid-time'>{remainingHours}</span>
                    <span className='ot-grid-label'>HOURS</span>
                  </div>
                  <div className="ot-grid-box">
                    <span className='ot-grid-time'>{remainingMinutes}</span>
                    <span className='ot-grid-label'>MINUTES</span>
                  </div>
                  <div className="ot-grid-box">
                    <span className='ot-grid-time'>{remainingSeconds}</span>
                    <span className='ot-grid-label'>SECONDS</span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Alert>) :
        (<Alert className={`thank-you-alert text-center text-white ${isLoading ? 'blur-bg' : ''}`} variant="success">
          Thank you. Your order has been received.
        </Alert>)
      }
      <Container className='mt-3'>
        {isLoading &&
          <div className='pageloader'>
            <Loader />
          </div>}
        <div className={`${isLoading ? 'blur-bg' : ''}`}>
          <ThankyouPageOffers order={order} />
          <Card className='my-5 border-0'>
            <Card.Header className='bg-white'>
              <div className='d-flex justify-content-between'>
                <div className='font-league-gothic fs-41px lh-50px me-2'>Your order</div>
                <Button
                  className='float-end bg-black font-league-gothic fs-23px'
                  size="lg"
                  as={Link as any}
                  to={`/my-account${QURLParams}`}
                >
                  My Account
                </Button>
              </div>
            </Card.Header>
            {order &&
              <Card.Body className='fs-5'>
                {deliveryDate &&
                  <Alert className='shadow-box bg-white text-black font-barlow fs-20px fw-700'>
                    <div className='d-flex'>
                      <div className='p-3 px-0 fs-6 w-60'>DELIVERY DATE:</div>
                      <div className='p-3 px-0 fs-6 fw-bold w-50'>{order.getDeliveryDate()}</div>
                    </div>
                  </Alert>
                }
                <div className='d-flex'>
                  <div className='p-3 w-40'>ORDER NUMBER:</div>
                  <div className='p-3 fw-bold w-60'>{order.data.number}</div>
                </div>
                <div className='d-flex'>
                  <div className='p-3 w-40'>EMAIL:</div>
                  <div className='p-3 fw-bold w-60'>{order.data.billing.email}</div>
                </div>
                <div className='d-flex'>
                  <div className='p-3 w-40'>TOTAL:</div>
                  <div className='p-3 fw-bold w-60'>${order.data.total}</div>
                </div>
                <div className='d-flex'>
                  <div className='p-3 w-40'>PAYMENT METHOD:</div>
                  <div className='p-3 fw-bold w-60'>{order.data.payment_method_title}</div>
                </div>
                <OrderDetails order={order} />
                <BillingAndShipping order={order} />
                {mightyPoints > 0 &&
                  <Alert className='shadow-box bg-white text-black font-barlow fs-20px'>
                    You have earned <span className='fw-bold'>{mightyPoints}</span>
                    &nbsp;Mighty Points for this order.
                  </Alert>
                }
                {/* {deliveryDate &&
              <DeliveryDetails order={order} />
            } */}
              </Card.Body>}
          </Card>
        </div>
      </Container >
    </div>
  );
}